/**
 * Pure readers for the SAK MFA claims the Custom Access Token Hook stamps on
 * every access token (`public.custom_access_token_hook`, #13125), shared by
 * the three consumers that must agree with the middlewares' enforcement:
 * `auth-context` (client, `atob`), the `/mfa/enroll` server page (`Buffer`)
 * and `MfaSettings`. Each caller decodes the token its own way and hands the
 * `app_metadata` object here, so the claim semantics live in ONE place.
 *
 * Contract: the hook writes booleans. Every check is a strict `=== true` —
 * a string `'true'` or a `1` is NOT a verdict (the hook never emits those, and
 * truthy coercion would misclassify regular users the day a claim shape
 * drifts).
 */

/** `app_metadata` as decoded from a Supabase access token (unknown shape). */
type JwtAppMetadata = Record<string, unknown> | null | undefined;

/**
 * The enforced SAK verdict: `professional_require_mfa`. True for a claimed
 * advocate profile, a confirmed email found in the SAK register, or a
 * self-declared advocate once the rollout grace instant has passed. This —
 * not `is_professional` (claimed profile only) — is what
 * `SupabaseJWTPayload.requires_mfa` and the Next.js middleware block AAL1 on.
 */
export function readProfessionalMfaRequired(
  appMetadata: JwtAppMetadata
): boolean {
  return appMetadata?.['professional_require_mfa'] === true;
}

export type ForcedReason = 'professional' | 'org' | null;

/**
 * Which forced-MFA trigger applies, for the "why are you here" copy.
 * Professional takes precedence — SAK binds the advocate regardless of org
 * membership. `is_professional` is kept alongside the verdict so a token
 * minted by the pre-#13125 hook (which emitted no verdict for anyone it did
 * not force) still renders the professional reason.
 */
export function readForcedReasonFromAppMetadata(
  appMetadata: JwtAppMetadata
): ForcedReason {
  if (
    readProfessionalMfaRequired(appMetadata) ||
    appMetadata?.['is_professional'] === true
  ) {
    return 'professional';
  }
  if (appMetadata?.['org_require_mfa'] === true) return 'org';
  return null;
}

interface MfaForcedInputs {
  /** Claimed advocate profile, from the backend's `/users/me`. */
  isProfessional: boolean;
  /** The JWT verdict, `readProfessionalMfaRequired`. */
  professionalMfaRequired: boolean;
  /** Active org membership — a coarse proxy for `org_require_mfa` (see MfaSettings). */
  isInOrg: boolean;
}

interface MfaForcedState {
  /** SAK binds this user: claimed profile OR the enforced verdict. */
  sakForced: boolean;
  /** Any forced trigger: SAK or org. Hides "Disable" in Settings. */
  mfaForced: boolean;
  /** Copy to render; SAK wins over org. */
  forcedReason: ForcedReason;
}

/**
 * The Settings panel's forced-MFA state (#2491, #2202, #13125). A forced
 * user must not see "Disable": the middleware would redirect them straight
 * back to `/mfa/enroll`. `isProfessional` alone would show it to an
 * email-matched or self-declared advocate, who is forced by the verdict only.
 */
export function deriveMfaForcedState(inputs: MfaForcedInputs): MfaForcedState {
  const sakForced = inputs.isProfessional || inputs.professionalMfaRequired;
  const mfaForced = sakForced || inputs.isInOrg;
  const forcedReason: ForcedReason = sakForced
    ? 'professional'
    : inputs.isInOrg
      ? 'org'
      : null;
  return { sakForced, mfaForced, forcedReason };
}
